import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, EyeOff, Trash2, Plus } from "lucide-react";
import { useState } from "react";
import { useSecurePrivacy } from "@/contexts/SecurePrivacyContext";
import { usePrefs } from "@/contexts/PrefsContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const Row = ({ label, hint, checked, onChange, testid }) => (
    <div className="flex items-start justify-between gap-6 py-3 border-b border-stone-100 dark:border-stone-800">
        <div>
            <div className="text-sm font-medium text-stone-900 dark:text-stone-100">{label}</div>
            {hint && <div className="text-xs text-stone-500 mt-1 max-w-md">{hint}</div>}
        </div>
        <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
);

export default function SettingsScreenPrivacy() {
    const {
        shield_enabled, hide_values_default, watermark_enabled, lock_on_blur,
        require_reauth, timeout_minutes, log_events, standalone,
        print_allowed_paths, default_sensitivity, updateSecurePrefs,
    } = useSecurePrivacy();
    const { prefs, updatePrefs } = usePrefs();
    const [newPath, setNewPath] = useState("");

    const patch = async (p, msg) => {
        await updateSecurePrefs(p);
        if (msg) toast.success(msg);
    };

    const addPath = async () => {
        const p = newPath.trim();
        if (!p || !p.startsWith("/")) { toast.error("Paths must start with /"); return; }
        const list = [...(print_allowed_paths || []), p];
        await patch({ print_allowed_paths: Array.from(new Set(list)) }, "Route added to print allowlist");
        setNewPath("");
    };
    const removePath = async (p) => {
        await patch({ print_allowed_paths: (print_allowed_paths || []).filter((x) => x !== p) }, "Route removed");
    };

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="settings-screen-privacy">
            <div className="mb-6 flex items-center gap-2 text-xs">
                <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Dashboard
                </Link>
                <span className="text-stone-300">/</span>
                <span className="text-stone-500">Settings</span>
                <span className="text-stone-300">/</span>
                <span className="font-medium text-stone-900 dark:text-stone-100">Security</span>
            </div>

            <div className="flex items-start gap-3 mb-2">
                <div className="mt-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 p-1.5">
                    <ShieldCheck size={16} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Screen Privacy</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Web browsers cannot block operating-system screenshots. VAULT provides <b>Screen
                        Privacy deterrence</b> &mdash; hiding sensitive information the moment the tab loses focus,
                        masking values on demand, and watermarking sensitive views. Extra protections are
                        available when VAULT is installed as a PWA or shipped as a native app.
                    </p>
                    {standalone && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/40 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300" data-testid="pwa-badge">
                            <ShieldCheck size={11} /> Running as installed PWA · Reauth-on-return available
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Automatic Privacy Shield</div>
                <Row label="Enable Privacy Shield" testid="opt-shield"
                    hint="Cover the screen with the VAULT logo when the tab is hidden, the window loses focus, or after inactivity."
                    checked={shield_enabled} onChange={(v) => patch({ shield_enabled: v }, v ? "Privacy Shield enabled" : "Privacy Shield disabled")} />
                <Row label="Lock when browser tab loses focus" testid="opt-lock-blur"
                    hint="Trigger the shield the instant this browser window is no longer active."
                    checked={lock_on_blur} onChange={(v) => patch({ lock_on_blur: v })} />
                <Row label="Require reauthentication when returning" testid="opt-reauth"
                    hint={standalone
                        ? "Ask for your password again after unlocking (verified via /api/auth/verify-password)."
                        : "Ask for your password on unlock. Enforced only when VAULT runs as an installed PWA or native app."}
                    checked={require_reauth} onChange={(v) => patch({ require_reauth: v })} />
                <div className="flex items-start justify-between gap-6 py-3">
                    <div>
                        <div className="text-sm font-medium">Privacy timeout</div>
                        <div className="text-xs text-stone-500 mt-1">Lock automatically after this many minutes of inactivity.</div>
                    </div>
                    <select
                        data-testid="opt-timeout"
                        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm"
                        value={timeout_minutes}
                        onChange={(e) => patch({ timeout_minutes: Number(e.target.value) })}
                    >
                        {[1, 2, 5, 10, 15, 30, 60].map((m) => (
                            <option key={m} value={m}>{m} min</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Hide values &amp; watermark</div>
                <Row label="Hide values by default" testid="opt-hide-default"
                    hint="Start every session with monetary values masked. You can toggle instantly with Shift + H."
                    checked={hide_values_default} onChange={(v) => patch({ hide_values_default: v })} />
                <Row label="Hide values now" testid="opt-hide-now"
                    hint="Immediately mask every balance, transaction, credit limit and card number in this session."
                    checked={!!prefs.hide_balance} onChange={(v) => updatePrefs({ hide_balance: v })} />
                <Row label="Watermark sensitive screens" testid="opt-watermark"
                    hint="Repeat a low-opacity diagonal watermark (masked email · timestamp · session ID). Density scales with page sensitivity."
                    checked={watermark_enabled} onChange={(v) => patch({ watermark_enabled: v })} />
                <div className="flex items-start justify-between gap-6 py-3">
                    <div>
                        <div className="text-sm font-medium">Default value sensitivity</div>
                        <div className="text-xs text-stone-500 mt-1 max-w-md">
                            Elements wrapped in <code>&lt;Sensitive&gt;</code> without an explicit level fall back to this classification.
                        </div>
                    </div>
                    <select
                        data-testid="opt-default-sensitivity"
                        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm"
                        value={default_sensitivity}
                        onChange={(e) => patch({ default_sensitivity: e.target.value })}
                    >
                        <option value="public">Public</option>
                        <option value="internal">Internal</option>
                        <option value="confidential">Confidential</option>
                        <option value="highly_sensitive">Highly Sensitive</option>
                    </select>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Print export allowlist</div>
                <p className="text-xs text-stone-500 mb-3 max-w-lg">
                    By default every authenticated screen is blocked from browser Print for privacy. Add specific
                    routes here to allow authorised printing (audit reports, invoices, tax packs). Wildcards
                    ending in <code>*</code> are supported.
                </p>
                <div className="flex gap-2">
                    <input
                        data-testid="opt-print-path-input"
                        className="flex-1 rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm"
                        placeholder="/reports  or  /statements/*"
                        value={newPath}
                        onChange={(e) => setNewPath(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPath())}
                    />
                    <button
                        data-testid="opt-print-path-add"
                        onClick={addPath}
                        className="inline-flex items-center gap-1 rounded-md bg-stone-900 text-stone-50 px-3 py-1.5 text-sm font-medium hover:bg-stone-800"
                    >
                        <Plus size={13} /> Add
                    </button>
                </div>
                <ul className="mt-3 space-y-1.5" data-testid="opt-print-path-list">
                    {(print_allowed_paths || []).length === 0 && (
                        <li className="text-xs text-stone-400">No routes on the allowlist. Every screen respects the print blackout.</li>
                    )}
                    {(print_allowed_paths || []).map((p) => (
                        <li key={p} className="flex items-center justify-between rounded-md border border-stone-100 dark:border-stone-800 px-3 py-1.5">
                            <code className="text-xs font-mono">{p}</code>
                            <button
                                onClick={() => removePath(p)}
                                data-testid={`opt-print-path-remove-${p.replace(/[^a-z0-9]/gi, "-")}`}
                                className="text-stone-400 hover:text-rose-600"
                                aria-label={`Remove ${p} from allowlist`}
                            >
                                <Trash2 size={13} />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Native app protections</div>
                <div className="text-sm text-stone-600 dark:text-stone-300 space-y-2">
                    <p className="flex items-start gap-2"><EyeOff size={14} className="mt-0.5 text-stone-400" />
                        <span><b>Android</b> · If installed as a native Android build (Capacitor / TWA), VAULT applies <code>FLAG_SECURE</code> to every activity. See <code>/app/mobile/README.md</code>.</span>
                    </p>
                    <p className="flex items-start gap-2"><EyeOff size={14} className="mt-0.5 text-stone-400" />
                        <span><b>iOS &amp; iPadOS</b> · Native build detects screen capture via <code>UIScreen.capturedDidChangeNotification</code>, replaces sensitive content with the Privacy Shield, and warns after a screenshot is detected.</span>
                    </p>
                    <p className="text-xs text-stone-500">
                        Web screenshot prevention cannot be fully guaranteed by browsers. We label this feature as <b>Screen Privacy Protection &middot; Web deterrence</b>, not &ldquo;screenshot blocked&rdquo;.
                    </p>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Data portability</div>
                <div className="flex items-start justify-between gap-6 py-2">
                    <div className="max-w-md">
                        <div className="text-sm font-medium text-stone-900 dark:text-stone-100">Export my VAULT</div>
                        <div className="text-xs text-stone-500 mt-1">
                            Download a signed ZIP of every record in your workspace. We&apos;ll email you a secure
                            link that expires in 24 hours.
                        </div>
                    </div>
                    <Link
                        to="/settings/data-export"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 px-4 py-1.5 text-[12px] font-semibold hover:opacity-90"
                        data-testid="link-data-export"
                    >
                        Open export
                    </Link>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-2">Audit</div>
                <Row label="Record privacy events" testid="opt-log-events"
                    hint="Log shield activations, screenshot detections and print attempts. Only event type and timestamp are recorded — never the captured image."
                    checked={log_events} onChange={(v) => patch({ log_events: v })} />
            </div>
        </div>
    );
}
